import {
    RealtimeEventData,
    SecurityAlertMedia,
    SecurityAlertRaised,
    SecurityAlertResolveInitiated,
    // SecurityAlertResolved,
    useSecurityAlertsNotifications,
} from "@lvt/security-alerts-notification-service-client";
import { List, Typography } from "@material-ui/core";
import { Alert as ToastAlert } from "@material-ui/lab";
import { SentimentSatisfiedAlt } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AlertService } from "@/api/services/interfaces/Alert";
import Failure from "@/common/containers/Failure/Failure";
import { Loading } from "@/common/containers/Loading";
import { useDispatch, useSelector } from "@/store";
import { addAlert, Severity } from "@/store/alerts/slice";
import { Alert, raisedNotificationToAlert, raisedNotificationToMedia, sortAlerts } from "@/utilities/types/alert";
import AlertItem from "@/views/alerts/AlertsPage/AlertItem/AlertItem";
import ResolveDialog from "@/views/alerts/AlertsPage/ResolveDialog/ResolveDialog";

import useStyles from "./AlertsPage.styles";


// Determines how long to wait before showing the alert banner in ms (if the service still hasn't connected)
export const DEFAULT_INITIAL_CONNECTION_TIME = 3000;

interface AlertsPageProps {
    alertService: AlertService;

    /**
     * Determines how long to wait before showing the alert banner in ms (if the service still hasn't connected)
     */
    initialConnectionTime?: number;
}

const AlertsPage: FC<AlertsPageProps> = ({ alertService, initialConnectionTime = DEFAULT_INITIAL_CONNECTION_TIME }: AlertsPageProps) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);
    // Flag representing if the alerts API has been fetched yet
    const [isInitialized, setIsInitialized] = useState(false);
    // Flag representing if the initial connection time has expired
    const [isInitialConnection, setIsInitialConnection] = useState(true);
    // Flag representing if the alerts fetch failed
    const [failed, setFailed] = useState(false);
    const [alerts, setAlerts] = useState<Alert[]>([]);
    const [isResolveOpen, setIsResolveOpen] = useState(false);
    const [resolveAlertDetails, setResolveAlertDetails] = useState<Alert>();

    const [securityAlertsCallbacks, securityAlertsConnectionState] = useSecurityAlertsNotifications();
    const { enqueueSnackbar } = useSnackbar();

    const { t } = useTranslation("translation", { keyPrefix: "components.alerts.alertsPage" });

    // Get alerts from the API
    useEffect(() => {
        alertService
            .getAlerts()
            .then((res) => {
                setAlerts(res);
                setIsInitialized(true);
            })
            .catch(() => {
                setFailed(true);

                dispatch(
                    addAlert({
                        severity: Severity.Error,
                        message: "Failed to retrieve alerts",
                    }),
                );
            });
    }, []);

    // Register realtime event callbacks
    useEffect(() => {
        const registerHandlers = () => {
            securityAlertsCallbacks.onSecurityAlertRaised((alert: RealtimeEventData<SecurityAlertRaised>) => {
                setAlerts((previous) => {
                    const existing = previous.find(a => a.alertId === alert.payload.alertId);

                    if (existing) return previous;

                    const next = [raisedNotificationToAlert(alert.payload), ...previous];

                    next.sort(sortAlerts);

                    return next;
                });
            });

            securityAlertsCallbacks.onSecurityAlertMediaAvailable((media: RealtimeEventData<SecurityAlertMedia>) => {
                setAlerts((previous) => {
                    const alertIndex = previous.findIndex(a => a.alertId === media.payload.alertId);

                    if (alertIndex === -1) return previous;

                    const alert = previous[alertIndex];

                    alert.media.push(raisedNotificationToMedia(media.payload));
                    previous[alertIndex] = alert;

                    return [...previous];
                });
            });

            // For the UI, an alert can be removed from the list when resolution has been initiated.
            securityAlertsCallbacks.onSecurityAlertResolveInitiated((resolveInitiated: RealtimeEventData<SecurityAlertResolveInitiated>) => {
                setAlerts((previous) => {
                    const existingIndex = previous.findIndex(a => a.alertId === resolveInitiated.payload.alertId);

                    if (existingIndex === -1) return previous;

                    previous.splice(existingIndex, 1);

                    return [...previous];
                });
            });

            // TODO: Use this after mvp to show alert resolution notification
            // securityAlertsCallbacks.onSecurityAlertResolved((resolved: RealtimeEventData<SecurityAlertResolved>) => {});
        };

        registerHandlers();
    }, []);

    // Set a timeout to prevent the disconnect banner from showing up during initial connection
    useEffect(() => {
        const timeoutId = setTimeout(() => setIsInitialConnection(false), initialConnectionTime);

        return () => clearTimeout(timeoutId);
    }, []);

    const handleClickResolve = (a: Alert) => {
        setResolveAlertDetails(a);
        setIsResolveOpen(true);
    };

    const classes = useStyles();

    useEffect(() => {
        if (securityAlertsConnectionState === "disconnected" && !isInitialConnection && !failed) {
            const alert = (
                <ToastAlert key="disconnected-alert-message" severity="error">
                    {t("errors.realtimeEvents")}
                </ToastAlert>
            );

            enqueueSnackbar(alert, {
                anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "right",
                },
                autoHideDuration: 12_000,
                key: "disconnected-alert-message",
                preventDuplicate: true,
                variant: Severity.Error,
            });
        }
    }, [securityAlertsConnectionState, isInitialConnection]);

    return (
        <>
            {/* Header */}
            <div className={classes.header}>
                <Typography variant="h5" className={classes.heading}>
                    {t("title")}
                </Typography>
            </div>

            {/* Content */}
            {isInitialized &&
                (alerts.length !== 0 ? (
                    <div className={classes.content}>
                        <List disablePadding className={classes.listArea}>
                            {alerts.map((a) => (
                                <AlertItem
                                    key={a.alertId}
                                    alert={a}
                                    onClickResolve={() => handleClickResolve(a)}
                                    showClientName={user.clientId !== a.clientId}
                                />
                            ))}
                        </List>
                        {resolveAlertDetails && <ResolveDialog open={isResolveOpen} setOpen={setIsResolveOpen} alertDetails={resolveAlertDetails} />}
                    </div>
                ) : (
                    <div className={classes.emptyContent}>
                        <div className={classes.emptyMessage}>
                            <div className={classes.emptyIconContainer}>
                                <SentimentSatisfiedAlt className={classes.emptyIcon} />
                            </div>
                            <Typography variant="h5">{t("noAlerts.header")}</Typography>
                            <Typography variant="body1">{t("noAlerts.message")}</Typography>
                        </div>
                    </div>
                ))}

            {/* Loading screen */}
            {!isInitialized && !failed && <Loading />}

            {/* Failure screen */}
            {failed && <Failure />}
        </>
    );
};

export default AlertsPage;
